import { phoneNumberValidation } from '@/common/validation';
import { AddressCheck } from '@/components/address-check';
import { Form } from '@/components/forms';
import { DwellingInput } from '@/components/forms/dwelling-input';
import { EmailInput } from '@/components/forms/email-input/component';
import { GenderInput } from '@/components/forms/gender-input';
import { AddressInput, DateInput } from '@/components/inputs';
import { Checkbox, Flex, NumberInput, Text, TextInput } from '@mantine/core';
import { FormikProps } from 'formik';
import { DateTime } from 'luxon';
import * as yup from 'yup';
import styles from './styles.module.css';

/*
	check that isOver18 is true if isParticipatingInPrizeDraw is true
	check that age is greater than 18 if isOver18 is true
*/

const validationSchema = yup.object({
	firstName: yup.string().required('First name is required').max(40, 'First name is too long'), // 40 chars Salesforce limitation
	lastName: yup.string().required('Last name is required').max(40, 'Last name is too long'), // 40 chars Salesforce limitation
	email: yup
		.string()
		.email('Invalid email address')
		.required('Email is required')
		.max(80, 'Email is too long'), // Salesforce limitation
	formattedAddress: yup.string().required('Please enter your address.'),
	postcode: yup.string().required('Please enter your postcode.'),
	phoneNumber: phoneNumberValidation.nullable(),
	gender: yup.string().nullable(),
	dateOfBirth: yup.date().when('isOver18', {
		is: (value: boolean) => value === true,
		then: (schema) =>
			schema
				.required('Date of Birth is required')
				.max(
					DateTime.now().minus({ years: 18 }).toJSDate(),
					'You have selected that you are over 18.'
				),
		otherwise: (schema) =>
			schema.notRequired().max(new Date(), 'Please enter a valid date of birth.'),
	}),
	dwellingType: yup.string().nullable(),
	householdSize: yup.number().nullable().moreThan(0, 'Please enter your household size.'),
	isParticipatingInPrizeDraw: yup.boolean().nullable().default(false),
	isOver18: yup.boolean().when('isParticipatingInPrizeDraw', {
		is: true,
		then: (schema) => schema.isTrue('You must be over 18 to participate in the prize draw'),
		otherwise: (schema) => schema.notRequired(),
	}),
});

export type AccountFormValues = {
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber?: string | null | undefined;
	formattedAddress?: string | null | undefined;
	addressLine1?: string | null | undefined;
	addressLine2?: string | null | undefined;
	city?: string | null | undefined;
	council?: string | null | undefined;
	postcode?: string | null | undefined;
	state?: string | null | undefined;
	gender?: string | null | undefined;
	dateOfBirth?: Date | null | undefined;
	dwellingType?: string | null | undefined;
	householdSize?: number | null | undefined;
	isOver18?: boolean | null | undefined;
	isParticipatingInPrizeDraw?: boolean | null | undefined;
	isManualAddress?: boolean | null | undefined;
	lat?: number | null | undefined;
	lng?: number | null | undefined;
};

type AccountFormProps = {
	onSubmit: (values: AccountFormValues) => void;
	formRef: React.RefObject<FormikProps<AccountFormValues>>;
	initialValues: AccountFormValues;
};

export const AccountForm = ({ formRef, onSubmit, initialValues }: AccountFormProps) => {
	return (
		<Form
			initialValues={initialValues}
			onSubmit={onSubmit}
			innerRef={formRef}
			validationSchema={validationSchema}
		>
			{(formikProps) => {
				const { values, setFieldValue, getFieldProps, touched, errors } = formikProps;
				return (
					<>
						<Text fz="24px" fw={900}>
							Basic Details
						</Text>
						<Flex gap="md" w="100%">
							<TextInput
								label="First Name"
								placeholder="First Name"
								className={styles.cell}
								{...getFieldProps('firstName')}
								error={touched.firstName && errors.firstName}
							/>
							<TextInput
								label="Last Name"
								placeholder="Last Name"
								className={styles.cell}
								{...getFieldProps('lastName')}
								error={touched.lastName && errors.lastName}
							/>
						</Flex>
						<EmailInput
							label="Email"
							placeholder="Email"
							{...getFieldProps('email')}
							error={touched.email && errors.email}
						/>
						<Text fz="24px" fw={900}>
							Address
						</Text>
						<AddressInput
							label="Enter Your Address"
							value={values.formattedAddress}
							error={touched.formattedAddress && errors.formattedAddress}
						/>

						<AddressCheck
							council={values.council}
							postcode={values.postcode}
							state={values.state}
							lat={values.lat}
							lng={values.lng}
						/>
						<Text fz="24px" fw={900}>
							Profile (Optional)
						</Text>
						<TextInput
							label="Phone Number"
							placeholder=""
							{...getFieldProps('phoneNumber')}
							error={touched.phoneNumber && errors.phoneNumber}
						/>
						<Flex gap="md">
							<GenderInput
								label="Gender"
								value={values.gender}
								onChange={(e) => setFieldValue('gender', e)}
								flex={1}
								error={touched.gender && errors.gender}
							/>
							<Flex gap={0} direction="column">
								<DateInput
									label="Date of Birth"
									value={values.dateOfBirth ?? undefined}
									onChange={(e) => setFieldValue('dateOfBirth', e)}
									flex={1}
									error={touched.dateOfBirth && errors.dateOfBirth}
									p="0"
								/>
								{!(touched.dateOfBirth && errors.dateOfBirth) && !!values.dateOfBirth && (
									<Text c="gray.0" size="xs" ml="sm">
										DD-MM-YYYY
									</Text>
								)}
							</Flex>
						</Flex>
						<Flex gap="md">
							<DwellingInput
								flex={1}
								label="Dwelling Type"
								value={values.dwellingType}
								onChange={(e) => setFieldValue('dwellingType', e)}
								error={touched.dwellingType && errors.dwellingType}
							/>
							<NumberInput
								flex={1}
								label="Household Size"
								placeholder=""
								value={values.householdSize ?? undefined}
								onChange={(e) => setFieldValue('householdSize', e === '' ? undefined : e)}
								error={touched.householdSize && errors.householdSize}
							/>
						</Flex>
						<Checkbox
							label="I wish to participate in the prize draw."
							checked={values.isParticipatingInPrizeDraw ?? false}
							onChange={(e) => setFieldValue('isParticipatingInPrizeDraw', e.target.checked)}
							error={touched.isParticipatingInPrizeDraw && errors.isParticipatingInPrizeDraw}
						/>
						<Checkbox
							label="I am over 18 years of age."
							checked={values.isOver18 ?? false}
							onChange={(e) => setFieldValue('isOver18', e.target.checked)}
							error={touched.isOver18 && errors.isOver18}
						/>
					</>
				);
			}}
		</Form>
	);
};
